import AgGridReactTable from "@components/ag-grid/AgGridReactTable";
import TableActionButton from "@components/buttons/TableActionButton";
import DataPill from "@components/pills/DataPill";
import AgGridSkeleton from "@components/ag-grid/AgGridSkeleton";
import LightCustomButton from "@components/buttons/LightCustomButton";
import GlobalTextInputField from "@components/inputs/GlobalTextInputField";
import { groupManagementApi } from "@services/apis/GroupManagement";
import {
  GetGroupMembersRequest,
  MembersData,
} from "@services/models/Groups/GetGroupMembers";
import {
  GetGroupsData,
  GetGroupsResponse,
} from "@services/models/Groups/GetGroups";
import { ColDef, RowClickedEvent } from "ag-grid-community";
import { useEffect, useMemo, useState } from "react";
import ConstantFunctions from "../../../utils/constants_functions";
import useSnackbarMessage from "../../../utils/HandleSuccessErrorSnackbars";
import AddCashierToGroupSlider from "../../group-management/components/AddCashierToGroupSlider";
import AddMerchantToGroupSlider from "../../group-management/components/AddMerchantToGroupSlider";
import CreateGroupSlider from "../../group-management/components/CreateGroupSlider";
import GroupPerformanceSlider from "../../group-management/components/GroupPerformanceSlider";

const MobileGroupManagement = () => {
  const constantFunctions = new ConstantFunctions();

  const [createGroupSliderOpen, setCreateGroupSliderOpen] =
    useState<boolean>(false);
  const [addCashierSliderOpen, setAddCashierSliderOpen] =
    useState<boolean>(false);
  const [addMerchantSliderOpen, setAddMerchantSliderOpen] =
    useState<boolean>(false);
  const [groupPerformanceSliderOpen, setGroupPerformanceSliderOpen] =
    useState<boolean>(false);
  const [groups, setGroups] = useState<GetGroupsData[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<GetGroupsData | null>(
    null,
  );
  const [merchants, setMerchants] = useState<MembersData[]>([]);
  const [cashiers, setCashiers] = useState<MembersData[]>([]);
  const [isLoadingGroups, setIsLoadingGroups] = useState<boolean>(false);
  const [isLoadingMerchants, setIsLoadingMerchants] = useState<boolean>(false);
  const [isLoadingCashiers, setIsLoadingCashiers] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [groupSearch, setGroupSearch] = useState<string>("");
  const [merchantSearch, setMerchantSearch] = useState<string>("");
  const [cashierSearch, setCashierSearch] = useState<string>("");
  const { handleError } = useSnackbarMessage();

  const groupsColumnDefs: ColDef[] = useMemo(
    () => [
      {
        headerName: "Created At",
        field: "createdAt",
        flex: 1,
        minWidth: 120,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agDateColumnFilter",
        cellRenderer: (params: any) => {
          return constantFunctions.dateFormatInDDMMYY(params.value);
        },
      },
      {
        headerName: "ID",
        field: "id",
        flex: 0.6,
        minWidth: 60,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agNumberColumnFilter",
      },
      {
        headerName: "Name",
        field: "name",
        flex: 1.5,
        minWidth: 120,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agTextColumnFilter",
        wrapText: true,
        autoHeight: true,
        cellStyle: {
          whiteSpace: "normal",
          wordBreak: "break-word",
          lineHeight: "1.2",
        },
      },
      {
        headerName: "Description",
        field: "description",
        flex: 2.5,
        minWidth: 200,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agTextColumnFilter",
        wrapText: true,
        autoHeight: true,
        cellStyle: {
          whiteSpace: "normal",
          wordBreak: "break-word",
          lineHeight: "1.2",
        },
      },
      {
        headerName: "# Merchants",
        field: "merchantCount",
        flex: 0.8,
        minWidth: 100,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agNumberColumnFilter",
      },
      {
        headerName: "# Cashiers",
        field: "cashierCount",
        flex: 0.8,
        minWidth: 100,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agNumberColumnFilter",
      },
      {
        headerName: "Action",
        field: "action",
        flex: 0.6,
        minWidth: 118,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        sortable: false,
        filter: false,
        cellRenderer: (params: any) => {
          return (
            <TableActionButton
              variant="danger"
              icon="delete"
              onClick={(event) => {
                event.stopPropagation();
                handleDeleteGroup(params.data.id);
              }}
            >
              Delete
            </TableActionButton>
          );
        },
      },
    ],
    [constantFunctions],
  );

  const merchantsColumnDefs: ColDef[] = useMemo(
    () => [
      {
        headerName: "ID",
        field: "memberId",
        flex: 0.8,
        minWidth: 80,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agNumberColumnFilter",
      },
      {
        headerName: "Name",
        field: "name",
        flex: 1.5,
        minWidth: 120,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agTextColumnFilter",
        wrapText: true,
        autoHeight: true,
        cellStyle: {
          whiteSpace: "normal",
          wordBreak: "break-word",
          lineHeight: "1.2",
        },
      },
      {
        headerName: "Email",
        field: "email",
        flex: 2,
        minWidth: 150,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agTextColumnFilter",
        wrapText: true,
        autoHeight: true,
        cellStyle: {
          whiteSpace: "normal",
          wordBreak: "break-word",
          lineHeight: "1.2",
        },
      },
      {
        headerName: "Status",
        field: "isActive",
        flex: 0.8,
        minWidth: 80,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agTextColumnFilter",
        cellRenderer: (params: any) => {
          const isActive = params.value;
          return (
            <DataPill value={isActive ? "active" : "inactive"} />
          );
        },
      },
      {
        headerName: "Action",
        field: "action",
        flex: 0.6,
        minWidth: 118,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        sortable: false,
        filter: false,
        cellRenderer: (params: any) => {
          return (
            <TableActionButton
              variant="danger"
              icon="delete"
              onClick={(event) => {
                event.stopPropagation();
                handleRemoveMember(
                  params.data.memberId,
                  "merchant",
                  params.data.groupId,
                );
              }}
            >
              Delete
            </TableActionButton>
          );
        },
      },
    ],
    [],
  );

  const cashiersColumnDefs: ColDef[] = useMemo(
    () => [
      {
        headerName: "ID",
        field: "memberId",
        flex: 0.8,
        minWidth: 80,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agNumberColumnFilter",
      },
      {
        headerName: "Name",
        field: "name",
        flex: 1.5,
        minWidth: 120,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agTextColumnFilter",
        wrapText: true,
        autoHeight: true,
        cellStyle: {
          whiteSpace: "normal",
          wordBreak: "break-word",
          lineHeight: "1.2",
        },
      },
      {
        headerName: "Email",
        field: "email",
        flex: 2,
        minWidth: 150,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agTextColumnFilter",
        wrapText: true,
        autoHeight: true,
        cellStyle: {
          whiteSpace: "normal",
          wordBreak: "break-word",
          lineHeight: "1.2",
        },
      },
      {
        headerName: "Status",
        field: "isActive",
        flex: 0.8,
        minWidth: 80,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agTextColumnFilter",
        cellRenderer: (params: any) => {
          const isActive = params.value;
          return (
            <DataPill value={isActive ? "active" : "inactive"} />
          );
        },
      },
      {
        headerName: "Action",
        field: "action",
        flex: 0.6,
        minWidth: 118,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        sortable: false,
        filter: false,
        cellRenderer: (params: any) => {
          return (
            <TableActionButton
              variant="danger"
              icon="delete"
              onClick={(event) => {
                event.stopPropagation();
                handleRemoveMember(
                  params.data.memberId,
                  "cashier",
                  params.data.groupId,
                );
              }}
            >
              Delete
            </TableActionButton>
          );
        },
      },
    ],
    [],
  );

  const onGroupRowClicked = (event: RowClickedEvent) => {
    const selectedGroupData = event.data as GetGroupsData;

    const allGroupRows = document.querySelectorAll(".ag-row[row-id*='group-']");
    allGroupRows.forEach((row) => {
      row.classList.remove("ag-row-selected-custom");
    });

    if (event.rowIndex !== null) {
      const groupRowElement = document.querySelector(
        `[row-id="group-${selectedGroupData.id}"]`,
      );
      if (groupRowElement) {
        groupRowElement.classList.add("ag-row-selected-custom");
      }
    }

    setSelectedGroup(selectedGroupData);
  };

  const filteredGroups = groups.filter((group) => {
    if (!groupSearch) return true;
    const searchTerm = groupSearch.toLowerCase();
    return (
      group.id.toString().includes(searchTerm) ||
      (group.name && group.name.toLowerCase().includes(searchTerm)) ||
      (group.description &&
        group.description.toLowerCase().includes(searchTerm))
    );
  });

  const filteredMerchants = merchants.filter((merchant) => {
    const searchTerm = merchantSearch.toLowerCase();
    return (
      merchant.memberId.toString().includes(searchTerm) ||
      (merchant.name && merchant.name.toLowerCase().includes(searchTerm)) ||
      (merchant.email && merchant.email.toLowerCase().includes(searchTerm))
    );
  });

  const filteredCashiers = cashiers.filter((cashier) => {
    const searchTerm = cashierSearch.toLowerCase();
    return (
      cashier.memberId.toString().includes(searchTerm) ||
      (cashier.name && cashier.name.toLowerCase().includes(searchTerm)) ||
      (cashier.email && cashier.email.toLowerCase().includes(searchTerm))
    );
  });

  useEffect(() => {
    fetchGroups();
  }, []);

  useEffect(() => {
    if (selectedGroup) {
      fetchGroupMembers("merchant");
      fetchGroupMembers("cashier");
    }
  }, [selectedGroup]);

  const fetchGroups = async () => {
    try {
      setIsLoadingGroups(true);
      setError("");
      const response: GetGroupsResponse = await groupManagementApi.getGroups();
      setGroups(response.data);
      if (response.data.length > 0) {
        setSelectedGroup(response.data[0]);
      }
    } catch (error: any) {
      setError(error?.response?.data?.msg || "Failed to fetch groups");
    } finally {
      setIsLoadingGroups(false);
    }
  };

  const fetchGroupMembers = async (roleType: "merchant" | "cashier") => {
    if (!selectedGroup) return;

    try {
      if (roleType === "merchant") {
        setIsLoadingMerchants(true);
      } else {
        setIsLoadingCashiers(true);
      }

      const request: GetGroupMembersRequest = {
        groupId: selectedGroup.id,
        roleType: roleType,
      };

      const response = await groupManagementApi.getGroupMembers(request);
      const members = response.data.members;

      if (roleType === "merchant") {
        setMerchants(members);
      } else {
        setCashiers(members);
      }
    } catch (error: any) {
      setError(error?.response?.data?.msg || `Failed to fetch ${roleType}s`);
    } finally {
      if (roleType === "merchant") {
        setIsLoadingMerchants(false);
      } else {
        setIsLoadingCashiers(false);
      }
    }
  };

  const handleRemoveMember = async (
    memberId: number,
    roleType: "merchant" | "cashier",
    groupId: number,
  ) => {
    console.log("handleRemoveMember called with:", {
      memberId,
      roleType,
      groupId,
      selectedGroup,
    });

    try {
      console.log("Making API call to removeMemberFromGroup with:", {
        groupId: groupId,
        memberId: memberId,
        roleType: roleType,
      });
      await groupManagementApi.removeMemberFromGroup({
        groupId: groupId,
        memberId: memberId,
        roleType: roleType,
      });
      console.log("API call successful, removing member from local state");

      if (roleType === "merchant") {
        setMerchants((prevMerchants) =>
          prevMerchants.filter((merchant) => merchant.memberId !== memberId),
        );
      } else {
        setCashiers((prevCashiers) =>
          prevCashiers.filter((cashier) => cashier.memberId !== memberId),
        );
      }

      setGroups((prevGroups) =>
        prevGroups.map((group) => {
          if (group.id === groupId) {
            return {
              ...group,
              merchantCount:
                roleType === "merchant"
                  ? group.merchantCount - 1
                  : group.merchantCount,
              cashierCount:
                roleType === "cashier"
                  ? group.cashierCount - 1
                  : group.cashierCount,
            };
          }
          return group;
        }),
      );

      if (selectedGroup && selectedGroup.id === groupId) {
        setSelectedGroup((prevSelected) => {
          if (!prevSelected) return null;
          return {
            ...prevSelected,
            merchantCount:
              roleType === "merchant"
                ? prevSelected.merchantCount - 1
                : prevSelected.merchantCount,
            cashierCount:
              roleType === "cashier"
                ? prevSelected.cashierCount - 1
                : prevSelected.cashierCount,
          };
        });
      }
    } catch (error: any) {
      console.error("Error removing member:", error);
      handleError({
        message: error?.response?.data?.msg || `Failed to remove ${roleType}`,
      });
    }
  };

  const handleDeleteGroup = async (groupId?: number) => {
    const idToDelete = groupId || selectedGroup?.id;
    if (!idToDelete) return;

    try {
      await groupManagementApi.deleteGroup({
        groupId: idToDelete,
      });

      setGroups((prevGroups) =>
        prevGroups.filter((group) => group.id !== idToDelete),
      );

      if (selectedGroup?.id === idToDelete) {
        setSelectedGroup(null);
        setMerchants([]);
        setCashiers([]);
      }
    } catch (error: any) {
      handleError({
        message: error?.response?.data?.msg || `Failed to delete group`,
      });
    }
  };

  return (
    <div className="flex flex-col h-screen">
      <div className="h-[56px] border-b border-border-subtle bg-bg-page-body flex flex-row justify-between items-center px-4 sticky top-0 bg-white z-10 w-full">
        <p className="text-text-base text-[14px] font-semibold">
          Group Management
        </p>
        <LightCustomButton
          onClick={() => setGroupPerformanceSliderOpen(true)}
          text="PERFORMANCE"
          textSize="11px"
          isActive={true}
        />
      </div>

      <div className="flex flex-col py-3 px-3 gap-4 overflow-auto flex-1">
        {error && (
          <div className="text-text-status-red p-2 bg-red-50 rounded text-[12px]">
            {error}
          </div>
        )}

        {/* Groups Section */}
        <div className="flex flex-col w-full">
          <div className="flex flex-col gap-2 mb-3">
            <div className="flex flex-row justify-between items-center">
              <span className="text-[12px] font-semibold">Groups</span>
              <LightCustomButton
                onClick={() => setCreateGroupSliderOpen(true)}
                text="+ GROUP"
                textSize="11px"
                isActive={true}
              />
            </div>
            <GlobalTextInputField
              value={groupSearch}
              onChange={(e) => setGroupSearch(e.target.value)}
              placeholder="Search Groups..."
              prefixIcon={
                <span className="material-symbols-outlined text-text-subtle text-[18px]">
                  search
                </span>
              }
            />
          </div>

          <div className="overflow-x-auto" style={{ height: "400px", width: "100%" }}>
            {isLoadingGroups && (!groups || groups.length === 0) ? (
              <AgGridSkeleton
                columnDefs={groupsColumnDefs}
                style={{ height: "100%", width: "100%" }}
              />
            ) : (
              <AgGridReactTable
                rowData={filteredGroups}
                columnDefs={groupsColumnDefs}
                onRowClicked={onGroupRowClicked}
                getRowId={(params) => `group-${params.data.id}`}
                compact={true}
                error={error || undefined}
              />
            )}
          </div>
        </div>

        {/* Merchants Section */}
        <div className="flex flex-col w-full">
          <div className="flex flex-col gap-2 mb-3">
            <div className="flex flex-row justify-between items-center">
              <span className="text-[12px] font-semibold">
                Merchants {selectedGroup ? `(${selectedGroup.name})` : ""}
              </span>
              <LightCustomButton
                onClick={() => setAddMerchantSliderOpen(true)}
                text="+ MERCHANT"
                textSize="11px"
                isActive={true}
              />
            </div>
            <GlobalTextInputField
              value={merchantSearch}
              onChange={(e) => setMerchantSearch(e.target.value)}
              placeholder="Search Merchants..."
              prefixIcon={
                <span className="material-symbols-outlined text-text-subtle text-[18px]">
                  search
                </span>
              }
            />
          </div>

          <div className="overflow-x-auto" style={{ height: "250px", width: "100%" }}>
            {isLoadingMerchants &&
            (!merchants || merchants.length === 0) ? (
              <AgGridSkeleton
                columnDefs={merchantsColumnDefs}
                style={{ height: "100%", width: "100%" }}
              />
            ) : (
              <AgGridReactTable
                rowData={filteredMerchants}
                columnDefs={merchantsColumnDefs}
                getRowId={(params) => `merchant-${params.data.rowid}`}
                compact={true}
                error={error || undefined}
              />
            )}
          </div>
        </div>

        {/* Cashiers Section */}
        <div className="flex flex-col w-full">
          <div className="flex flex-col gap-2 mb-3">
            <div className="flex flex-row justify-between items-center">
              <span className="text-[12px] font-semibold">
                Cashiers {selectedGroup ? `(${selectedGroup.name})` : ""}
              </span>
              <LightCustomButton
                onClick={() => setAddCashierSliderOpen(true)}
                text="+ CASHIER"
                textSize="11px"
                isActive={true}
              />
            </div>
            <GlobalTextInputField
              value={cashierSearch}
              onChange={(e) => setCashierSearch(e.target.value)}
              placeholder="Search Cashiers..."
              prefixIcon={
                <span className="material-symbols-outlined text-text-subtle text-[18px]">
                  search
                </span>
              }
            />
          </div>

          <div className="overflow-x-auto" style={{ height: "250px", width: "100%" }}>
            {isLoadingCashiers && (!cashiers || cashiers.length === 0) ? (
              <AgGridSkeleton
                columnDefs={cashiersColumnDefs}
                style={{ height: "100%", width: "100%" }}
              />
            ) : (
              <AgGridReactTable
                rowData={filteredCashiers}
                columnDefs={cashiersColumnDefs}
                getRowId={(params) => `cashier-${params.data.rowid}`}
                compact={true}
                error={error || undefined}
              />
            )}
          </div>
        </div>
      </div>

      <CreateGroupSlider
        isOpen={createGroupSliderOpen}
        onClose={() => setCreateGroupSliderOpen(false)}
        onGroupCreated={fetchGroups}
      />
      <AddMerchantToGroupSlider
        isOpen={addMerchantSliderOpen}
        onClose={() => setAddMerchantSliderOpen(false)}
        onMerchantAdded={() => {
          if (selectedGroup) {
            fetchGroupMembers("merchant");
          }
          fetchGroups();
        }}
        selectedGroupId={selectedGroup?.id.toString() || ""}
        selectedGroupName={selectedGroup?.name || ""}
      />
      <AddCashierToGroupSlider
        isOpen={addCashierSliderOpen}
        onClose={() => setAddCashierSliderOpen(false)}
        onCashierAdded={() => {
          if (selectedGroup) {
            fetchGroupMembers("cashier");
          }
          fetchGroups();
        }}
        selectedGroupId={selectedGroup?.id.toString() || ""}
        selectedGroupName={selectedGroup?.name || ""}
      />
      {groups && (
        <GroupPerformanceSlider
          isOpen={groupPerformanceSliderOpen}
          onClose={() => setGroupPerformanceSliderOpen(false)}
          groups={groups}
        />
      )}
    </div>
  );
};

export default MobileGroupManagement;
